import { useQuery } from '@apollo/client/react';
import { usePermissions } from '@/features/auth';
import { useFeatureFlags } from '@/features/feature-flags';
import { GetProfileDocument } from '@/graphql';
import { accountActionsSubjectOf } from '@/features/admin/components/system-settings/account/account-actions';
import {
  SETTINGS_SECTIONS,
  isSettingsSectionDecided,
  visibleSettingsSections,
  type SettingsSection,
  type SettingsSectionId,
  type SettingsVisibilityContext
} from './settings-sections';

export interface SettingsSectionsState {
  /** False until the flags and the profile have loaded; the menu waits for it. */
  readonly ready: boolean;
  /** The sections the current person may open, in menu order. */
  readonly sections: readonly SettingsSection[];
  /** The sections whose visibility is already final: all once ready, else those needing no more. */
  readonly decided: readonly SettingsSectionId[];
}

/** The System settings sections the signed-in person may see. */
export function useSettingsSections(): SettingsSectionsState {
  const { hasPermission } = usePermissions();
  const { isEnabled, isReady } = useFeatureFlags();
  const { data, error } = useQuery(GetProfileDocument, { fetchPolicy: 'cache-first' });
  // Once answered, a refetch keeps the open section; an unreadable profile hides billing and
  // account management.
  const profileAnswered = data !== undefined || error !== undefined;
  const account = error === undefined ? data?.profile?.account : undefined;

  const context: SettingsVisibilityContext = {
    hasPermission,
    isFlagEnabled: isEnabled,
    isManagedClient: error !== undefined || !!account?.managingMasterAccount,
    account: accountActionsSubjectOf(account)
  };
  const ready = isReady && profileAnswered;
  const pending = { flags: !isReady, profile: !profileAnswered };

  return {
    ready,
    sections: visibleSettingsSections(context),
    decided: SETTINGS_SECTIONS.filter(
      section => ready || isSettingsSectionDecided(section, context, pending)
    ).map(section => section.id)
  };
}
